import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Eye, Hand, Ear, Wind as Nose, Sparkles, CheckCircle, RotateCcw } from "lucide-react";

const SENSES = [
  { sense: "See", count: 5, icon: Eye, prompt: "Name 5 things you can see right now.", placeholder: "e.g., My desk lamp, a tree outside..." },
  { sense: "Touch", count: 4, icon: Hand, prompt: "Name 4 things you can physically feel.", placeholder: "e.g., The chair under me, keyboard..." },
  { sense: "Hear", count: 3, icon: Ear, prompt: "Name 3 things you can hear.", placeholder: "e.g., Traffic outside, my breathing..." },
  { sense: "Smell", count: 2, icon: Nose, prompt: "Name 2 things you can smell.", placeholder: "e.g., Coffee, fresh air..." },
  { sense: "Taste", count: 1, icon: Sparkles, prompt: "Name 1 thing you can taste.", placeholder: "e.g., Mint toothpaste..." },
];

const GroundingTool = () => {
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentInput, setCurrentInput] = useState("");
  const [complete, setComplete] = useState(false);

  const current = SENSES[step];
  const entries = responses[step] || [];
  const Icon = current?.icon || Eye;

  const addEntry = () => {
    if (!currentInput.trim()) return;
    const updated = [...entries, currentInput.trim()];
    setResponses({ ...responses, [step]: updated });
    setCurrentInput("");
    if (updated.length >= current.count) {
      if (step < SENSES.length - 1) setTimeout(() => setStep(step + 1), 400);
      else setTimeout(() => setComplete(true), 400);
    }
  };

  const reset = () => { setStep(0); setResponses({}); setCurrentInput(""); setComplete(false); };

  if (complete) {
    return (
      <div data-testid="grounding-tool" className="rounded-2xl p-8 text-center" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "var(--ll-green)" }}>
          <CheckCircle size={28} style={{ color: "white" }} />
        </div>
        <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>You're Grounded</h2>
        <p className="text-sm mb-6" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
          Well done. Take a moment to notice how you feel now compared to when you started.
        </p>
        <button onClick={reset} className="rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2 mx-auto" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          <RotateCcw size={14} /> Start Again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="grounding-tool" className="rounded-2xl p-8" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>5-4-3-2-1 Sensory Grounding</h2>
      <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
        This technique engages all five senses to anchor you in the present moment, interrupting anxiety and overthinking.
      </p>

      {/* Progress */}
      <div className="flex gap-1.5 mb-8">
        {SENSES.map((s, i) => (
          <div key={s.sense} className="flex-1 h-1.5 rounded-full transition-all duration-500" style={{ background: i < step ? "var(--ll-green)" : i === step ? "var(--ll-accent)" : "var(--ll-bg-secondary)" }} />
        ))}
      </div>

      {/* Current sense */}
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4" style={{ background: "var(--ll-bg-secondary)" }}>
          <Icon size={28} style={{ color: "var(--ll-green)" }} />
        </div>
        <p className="font-serif text-3xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>
          {current.count} — {current.sense}
        </p>
        <p className="text-sm mb-6" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{current.prompt}</p>

        {/* Entries */}
        <div className="flex flex-wrap gap-2 justify-center mb-4 min-h-[40px]">
          {entries.map((e, i) => (
            <span key={`${step}-${e}-${i}`} className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(163,184,153,0.2)", color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              {e}
            </span>
          ))}
        </div>

        {entries.length < current.count && (
          <div className="flex gap-2 max-w-sm mx-auto">
            <Input data-testid="grounding-input" value={currentInput} onChange={(e) => setCurrentInput(e.target.value)}
              placeholder={current.placeholder}
              onKeyDown={(e) => e.key === "Enter" && addEntry()}
              className="rounded-xl border" style={{ borderColor: "var(--ll-border)" }} />
            <button onClick={addEntry} className="rounded-xl px-4 text-sm font-medium flex-shrink-0" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
              Add
            </button>
          </div>
        )}
        <p className="text-xs mt-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{entries.length}/{current.count} entered</p>
      </div>
    </div>
  );
};

export default GroundingTool;
